// SPDX-License-Identifier: MIT
// Local project delivery (desktop). The web hands a finished project off via
// "push to Forgejo → clone → claude" ; the desktop scaffolds it straight onto
// the user's OWN disk and opens a terminal there, so they continue with their
// OWN Claude. BYOAI to the end — nothing leaves the machine. The Rust side
// (deliver_project / open_terminal) picks the folder, writes the files + git
// init, and spawns the terminal; this module builds the files + wraps invoke.
import type { MagnifyResult } from '../auth/magnify';
import type { Locale } from '../i18n/detect';
import { tauriInvoke, tauriChannelCtor } from './tauri';
import { classifyPushResult, type PushStatus, type GitResult } from '../projects/code-push';

export interface DeliveryFile {
  name: string;
  content: string;
}

// The scaffolded file set, derived purely from the fiche + idea:
//  - .gitattributes : pins LF (text=auto eol=lf) so a Windows checkout doesn't churn EOLs
//  - CLAUDE.md  : the fiche framed as working context for the user's Claude Code
//  - README.md  : the human-readable fiche
//  - genesis.json : the structured manifest (machine-readable provenance)
//  - .env.local  : (optional) wires the app to its provisioned backend when creds are present
export function buildDeliveryFiles(result: MagnifyResult, idea: string, locale: Locale, backendUrl?: string | null, backendAnonKey?: string | null): DeliveryFile[] {
  const outline = result.outline.map((o) => `- ${o}`).join('\n');
  const steps = result.first_steps.map((s, i) => `${i + 1}. ${s}`).join('\n');
  const stack = result.stack ?? (locale === 'fr' ? '(à décider)' : '(to decide)');

  const claude = `# ${result.title}

> Fiche projet générée par Genesis. Ce fichier donne le contexte à Claude Code :
> ouvre un terminal ici et lance \`claude\` pour continuer sur TON abonnement.

## Idée de départ
${idea}

## Ce que l'utilisateur peut faire
${outline}

## Premières étapes (chacune < 1h)
${steps}

## Stack
${stack}
`;

  const readme = `# ${result.title}

${idea}

## Fonctionnalités
${outline}

## Pour démarrer
${steps}

## Stack
${stack}

---
Généré par [Genesis](https://genesis.myconciergerie.fr).
`;

  const manifest = {
    title: result.title,
    idea,
    outline: result.outline,
    first_steps: result.first_steps,
    stack: result.stack ?? null,
    locale,
    generated_by: 'Genesis',
    generated_at: new Date().toISOString(),
  };

  const files: DeliveryFile[] = [
    // Pin LF for all text files so a Windows checkout/clone (core.autocrlf) does
    // not flip every line ending and flood the working tree with phantom CRLF
    // churn. Ships from the first commit so every generated repo is cross-platform
    // clean. cf genesis_continuity_scaffold_eol_churn_and_nested_clone (ascendis).
    { name: '.gitattributes', content: '* text=auto eol=lf\n' },
    { name: 'CLAUDE.md', content: claude },
    { name: 'README.md', content: readme },
    { name: 'genesis.json', content: JSON.stringify(manifest, null, 2) },
  ];
  const env = envLocalFile(backendUrl, backendAnonKey);
  if (env) files.push(env);
  return files;
}

// Open the native folder picker, scaffold the files + git init under a slug of
// the title, and return the created directory path — or null if the user
// cancelled the picker.
export async function deliverProject(title: string, files: DeliveryFile[]): Promise<string | null> {
  const path = await tauriInvoke()('deliver_project', { title, files });
  return typeof path === 'string' ? path : null;
}

// Build the `.env.local` DeliveryFile that wires a generated app to its backend.
// Returns null when the project has no backend creds (delivery then omits it and
// the agent uses local storage per the agentBackendNone guardrail). The two keys
// match what buildAgentBuildTask's agentBackendConfigured clause tells the agent.
export function envLocalFile(url: string | null | undefined, anonKey: string | null | undefined): DeliveryFile | null {
  if (!url || !anonKey) return null;
  return {
    name: '.env.local',
    content: `VITE_SUPABASE_URL=${url}\nVITE_SUPABASE_ANON_KEY=${anonKey}\n`,
  };
}

// Write/merge the backend creds into an already-delivered project dir's .env.local
// (the provision-after-delivery path). Calls the path-jailed Rust write_project_env.
export async function writeProjectEnv(dir: string, url: string, anonKey: string): Promise<void> {
  await tauriInvoke()('write_project_env', { dir, url, anonKey });
}

// Open the OS terminal in the given directory.
export async function openTerminal(path: string): Promise<void> {
  await tauriInvoke()('open_terminal', { path });
}

// Agentic bridge: run the user's Claude Code as a real agent INSIDE the delivered
// project directory (tools on, permissions bypassed, propose-then-execute system
// prompt — see the Rust claude_agent command). Returns the raw `--output-format
// json` envelope; the caller parses the model's text via parseClaudeOutput. This is
// how the desktop "takes charge" instead of telling the user to type commands.
export async function runAgent(prompt: string, projectDir: string): Promise<string> {
  return (await tauriInvoke()('claude_agent', { prompt, projectDir })) as string;
}

// STREAMING agent: the Rust claude_agent_stream forwards each raw NDJSON line from
// `claude --output-format stream-json` over a Tauri Channel, then a final done/error.
// The caller parses lines with parseClaudeStreamLine and drives a live UI. The
// returned promise resolves when the command returns (after Done) — but the UI
// updates from onMsg as it streams, long before that.
export type AgentChannelMsg =
  | { event: 'line'; data: { line: string } }
  | { event: 'done'; data: { code: number } }
  | { event: 'error'; data: { message: string } };

export function runAgentStream(
  prompt: string,
  projectDir: string,
  onMsg: (msg: AgentChannelMsg) => void,
  // true on a FOLLOW-UP turn → `claude --continue` so the agent keeps the build context
  // (memory across prompts). false / omitted for the initial auto-build (fresh).
  continueConversation = false,
): Promise<void> {
  const Channel = tauriChannelCtor();
  const ch = new Channel<AgentChannelMsg>();
  ch.onmessage = onMsg;
  // Rust arg `on_event` ↔ JS `onEvent` ; `project_dir` ↔ `projectDir` ;
  // `continue_conversation` ↔ `continueConversation` (Tauri maps case).
  return tauriInvoke()('claude_agent_stream', { prompt, projectDir, continueConversation, onEvent: ch }) as Promise<void>;
}

// Stop the in-flight streaming agent (kills the child process). No-op if none.
export function cancelAgent(): Promise<void> {
  return tauriInvoke()('claude_agent_cancel') as Promise<void>;
}

// Result of the clone-first delivery primitive. `cancelled` = the user dismissed
// the folder picker. Otherwise `path` is the cloned project dir (null on a clone
// failure → caller falls back to a local scaffold), and `status` classifies the
// git outcome (the same honest classifier used for pushes).
export interface CloneOutcome { cancelled: boolean; path: string | null; status: PushStatus; detail: string; }

// Open the folder picker and clone the project's bootstrapped Forgejo repo into
// <picked>/<slug(title)>, authenticating with a TRANSIENT per-repo READ token.
// This is the clone-first deliver path: the user works on the REAL code + history
// instead of a fresh scaffold, so builds auto-push fast-forward and the code
// follows them across workstations. Never throws — failures come back as status.
export async function cloneProject(title: string, repoUrl: string, token: string): Promise<CloneOutcome> {
  try {
    const r = (await tauriInvoke()('clone_project', { title, repoUrl, token })) as {
      cancelled: boolean; path: string | null; code: number; stderr: string;
    };
    if (r.cancelled) return { cancelled: true, path: null, status: 'error', detail: '' };
    return { cancelled: false, path: r.path, status: classifyPushResult({ code: r.code, stderr: r.stderr }), detail: r.stderr };
  } catch (e) {
    return { cancelled: false, path: null, status: 'error', detail: String(e) };
  }
}

// Result of adopt-existing-folder (C6). `cancelled` = picker dismissed. `valid`
// = the picked folder belongs to this project (genesis.json or matching git
// remote). The caller warns on a mismatch instead of adopting a stranger.
export interface AdoptOutcome { cancelled: boolean; path: string | null; valid: boolean; }

// Pick an existing local folder and validate it is this project's working copy
// (covers the "I already cloned it in WSL" case). `ownerRepo` is "<owner>/<repo>"
// derived from forgejo_repo_url. Never throws — failures come back as not-valid.
export async function adoptProjectFolder(ownerRepo: string): Promise<AdoptOutcome> {
  try {
    return (await tauriInvoke()('adopt_project_folder', { ownerRepo })) as AdoptOutcome;
  } catch {
    return { cancelled: false, path: null, valid: false };
  }
}

export interface PushOutcome { status: PushStatus; detail: string; }

// Pousse le code du dossier projet vers son repo Forgejo. NON-BLOQUANT côté
// appelant : les erreurs reviennent comme statut, jamais comme throw qui
// casserait le build. Rust renvoie le {code, stderr} brut ; on classe TS-side.
export async function pushProjectCode(
  dir: string, repoUrl: string, token: string, message: string,
): Promise<PushOutcome> {
  try {
    const r = (await tauriInvoke()('project_git_push', { dir, repoUrl, token, message })) as GitResult;
    return { status: classifyPushResult(r), detail: r.stderr };
  } catch (e) {
    return { status: 'error', detail: String(e) };
  }
}
